import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Login = () => {
  const [isSignup, setIsSignup] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSignup) {
      const result = await signup(formData.email, formData.password, formData.name);
      if (result.success) {
        toast.success('Account created successfully!');
        navigate('/');
      } else {
        toast.error(result.error || 'Signup failed');
      }
    } else {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        toast.success('Welcome back!');
        navigate('/');
      } else {
        toast.error(result.error || 'Login failed');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md border-gray-200">
        <CardHeader className="space-y-1 text-center">
          <div className="flex items-center justify-center space-x-3 mb-2">
            {/* Hookah Crest Logo with 3D Effect */}
            <div className="relative group">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FFD700] to-[#B8860B] blur-md opacity-50"></div>
              <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] via-[#FFD700] to-[#C5A028] flex items-center justify-center shadow-2xl border-2 border-[#FFD700]/30">
                <div className="absolute inset-1 rounded-full bg-gradient-to-br from-white/20 to-transparent"></div>
                <svg
                  viewBox="0 0 64 64"
                  fill="none"
                  className="w-9 h-9 relative z-10"
                >
                  {/* Hookah Base */}
                  <path
                    d="M32 48C28 48 25 45 25 42L25 38L39 38L39 42C39 45 36 48 32 48Z"
                    fill="#0a1128"
                    opacity="0.9"
                  />
                  {/* Water in base */}
                  <ellipse cx="32" cy="38" rx="7" ry="2" fill="#4A90E2" opacity="0.6"/>
                  
                  {/* Hookah Stem */}
                  <rect x="30" y="25" width="4" height="13" fill="#0a1128" opacity="0.9"/>
                  
                  {/* Bowl */}
                  <path
                    d="M27 20C27 18 29 16 32 16C35 16 37 18 37 20L37 25L27 25L27 20Z"
                    fill="#0a1128"
                    opacity="0.9"
                  />
                  
                  {/* Hot Coals */}
                  <circle cx="29" cy="18" r="1.5" fill="#FF6B35"/>
                  <circle cx="32" cy="17" r="1.5" fill="#FF8C42"/>
                  <circle cx="35" cy="18" r="1.5" fill="#FF6B35"/>
                  
                  {/* Smoke/Flame effect - wavy abstract */}
                  <path
                    d="M32 15 Q30 12 31 9 Q32 11 32 8 Q32 11 33 9 Q34 12 32 15"
                    stroke="#D4AF37"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.7"
                  />
                  <path
                    d="M28 14 Q27 11 28 9"
                    stroke="#FFD700"
                    strokeWidth="0.8"
                    fill="none"
                    opacity="0.6"
                  />
                  <path
                    d="M36 14 Q37 11 36 9"
                    stroke="#FFD700"
                    strokeWidth="0.8"
                    fill="none"
                    opacity="0.6"
                  />
                  
                  {/* Hoses */}
                  <path
                    d="M25 35 Q20 35 18 37"
                    stroke="#0a1128"
                    strokeWidth="2"
                    fill="none"
                    opacity="0.8"
                  />
                  <path
                    d="M39 35 Q44 35 46 37"
                    stroke="#0a1128"
                    strokeWidth="2"
                    fill="none"
                    opacity="0.8"
                  />
                </svg>
                {/* Bottom shine effect */}
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-8 h-2 bg-white/40 rounded-full blur-sm"></div>
              </div>
            </div>
            <div className="text-2xl font-display font-bold text-[#0a1128]">
              Smithhookahsupplyco
            </div>
          </div>
          <CardTitle className="text-2xl font-semibold">
            {isSignup ? 'Create an account' : 'Welcome back'}
          </CardTitle>
          <p className="text-sm text-gray-600">
            {isSignup
              ? 'Sign up to start shopping'
              : 'Sign in to your account to continue'
            }
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignup && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="John Doe"
                />
              </div>
            )}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-[#0a1128] font-semibold h-11"
            >
              {isSignup ? 'Sign Up' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsSignup(!isSignup)}
              className="text-sm text-[#D4AF37] hover:underline"
            >
              {isSignup
                ? 'Already have an account? Sign in'
                : "Don't have an account? Sign up"
              }
            </button>
          </div>

          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-gray-600 hover:text-[#D4AF37]">
              Continue as guest
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;